import {ArrayFilterOperation} from 'shared/models/array';

export enum UserRole {
    TenantAdmin = 'TENANT_ADMIN',
    Admin = 'ADMIN',
    Student = 'STUDENT',
    Teacher = 'TEACHER',
    Parent = 'PARENT',
}

export enum UserCreatedByType {
    DEFAULT = 'DEFAULT',
    ADMIN = 'ADMIN',
    SIGNUP = 'SIGNUP',
}

export enum UserTitle {
    Mr = 'Mr',
    Ms = 'Ms',
    Mrs = 'Mrs',
}

export enum IUserPaymentStatus {
    FREE_USER = 'FREE_USER',
    PAYING_USER = 'PAYING_USER',
    EXEMPT_USER = 'EXEMPT_USER',
}

export interface ITenantUserKey {
    tenantId: number;
    userId: number;

    [key: string]: number;
}

export interface IUserImage {
    file_id?: number;
    url?: string;
    avatar?: string;
    filename?: string;
}

export interface IUserPermissions {
    system_edit: boolean
    tenant_edit: boolean
    self_edit: boolean
    assignment_edit: boolean
    assignment_delete: boolean
    course_edit: boolean
    course_delete: boolean
    course_archived_edit: boolean
    progress_edit: boolean
    progress_view: boolean
    users_create: boolean
    users_delete: boolean
    users_view: boolean
    users_archived_edit: boolean
    admin_create: boolean
    live_session_edit: boolean
    live_session_delete: boolean
    tag_edit: boolean
    tag_delete: boolean
    groups_edit: boolean
    groups_delete: boolean

    [key: string]: boolean;
}

export interface IUser {
    id: number;
    tenant_id: number;
    firstname: string;
    lastname: string;
    title?: UserTitle;
    byline: string;
    email: string;
    username?: string;
    phone_number?: string;
    tag_ids: number[];
    roles: UserRole[];
    permissions: IUserPermissions;
    image?: IUserImage;
    is_archived: boolean;
    is_deleted: boolean;
    is_active: boolean;
    author_id: number;
    create_type: string;
    created_at: string;
    updated_at: string;
    deleted_at: string;
    language?: string;
    metadata?: IUserMetadata;
    managed_by_id: number;
    managed_user_ids: number[];
}

export interface IUpdateUser {
    firstname?: string;
    lastname?: string;
    title?: UserTitle;
    username?: string;
    byline?: string;
    email?: string;
    phone_number?: string;
    password?: string;
    image?: IUserImage;
    permissions?: IUserPermissions;
    language?: string;
    metadata?: IUserMetadataUpdate;
}

export interface IUserSettingsForm {
    firstname: string;
    lastname: string;
    byline?: string;
    email: string;
    username?: string;
    password?: string;
    phone_number?: string;
    image?: IUserImage;
}

export interface IUserCreate {
    firstname: string;
    lastname: string;
    title?: UserTitle;
    byline?: string;
    email: string;
    username?: string;
    password: string;
    phone_number: string;
    tag_ids: number[];
    roles: UserRole[];
    permissions: string[];
    image?: IUserImage;
    managed_by_id?: number;
}

export interface IResetPasswordCompleteInput {
    tenantId: number;
    tokenId: string;
    password: string;
}

export interface IUserListResult {
    users: IUser[];
    more: boolean;
    next_cursor: string;
}

export interface IUserListQuery {
    tenantId: number;
    cursor?: string;
    limit: number;
    role?: UserRole;
    search_term?: string;
    tag_ids?: number[];
    tags_op?: ArrayFilterOperation;
    managed_by_id?: number;
}

export interface IUpdateUserInput {
    accessToken?: string;
    tenantId: number;
    userId: number;
    updateData: IUpdateUser | IUserSettingsForm;
}

export interface IUserListFilters {
    limit: number;
    page: number;
    role?: UserRole;
    searchTerm?: string;
    tagIds?: number[];
    tagOp?: ArrayFilterOperation;

    [key: string]: number | UserRole | undefined | string | number[];
}

export type IUserListFiltersUpdate = Partial<IUserListFilters>;

export interface IBatchDeleteUsers {
    tenantId: number;
    user_ids: number[];
}

export interface IUserSummary {
    id: number;
    tenant_id: number;
    firstname: string;
    lastname: string;
    roles: UserRole[];
    image?: IUserImage;
    is_archived: boolean;
}

export interface IUserCountResult {
    user_count: number;
}

export interface IUserIdsListResult {
    user_ids: number[];
    more: boolean;
    next_cursor: string;
}

export interface IUserIdsListQuery {
    role?: UserRole;
    search_term?: string;
    tag_ids?: number[];
    tags_op?: ArrayFilterOperation;

    [key: string]: UserRole | undefined | string | number[];
}

export interface IUserBatchQuery {
    user_ids: number[];
}

export interface IBatchUserResult {
    users: IUser[];
}

export interface IUserMetadata {
    is_registered: boolean;
    payment_status?: IUserPaymentStatus;
    payment_status_updated_at?: string;
    seen_default_welcome: boolean;
}

export interface IUserMetadataUpdate {
    is_registered?: boolean;
    payment_status?: IUserPaymentStatus;
    payment_status_updated_at?: string;
    seen_default_welcome?: boolean;
}